import { type ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useClickOutside } from '../lib/useClickOutside'
import { useFocusTrap } from '../lib/useFocusTrap'

/** Diálogo centrado sobre un fondo oscuro. Se cierra con Escape o clickeando
 *  afuera — mismo hook que usan los pickers de checkboxes, reutilizado acá
 *  porque "afuera del recuadro" es exactamente lo que necesita detectar.
 *
 *  Se monta en `document.body` con un portal: los modales que se abren desde
 *  la barra superior (menú de usuario, campana de alertas, asesor) estaban
 *  dentro de `.app-topbar`, que tiene `backdrop-filter` y eso vuelve al
 *  contenedor el bloque de referencia de los `position: fixed`. El fondo
 *  quedaba del alto de la barra y el modal se cortaba arriba. */
export function Modal({
  onClose,
  children,
  className,
}: {
  onClose: () => void
  children: ReactNode
  /** Modificador de ancho (ej. "modal--narrow") para un contenido chico —
   *  el default (720px) es para formularios largos, no para dos campos. */
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, onClose, true)
  useFocusTrap(ref, true)

  return createPortal(
    <div className="modal-backdrop">
      <div
        className={`modal${className ? ` ${className}` : ''}`}
        ref={ref}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
